import restClient from 'config/httpClient';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { redirect } from 'utils/utils';
import requestLoopExp from 'utils/fetch';
import { createNotification } from 'modules/notifications/actions';

import * as actions from './actions';
import { getClientDocumentsTask, getRentalCarTask } from './selectors';

// Checklists recompute asynchronously server-side — poll with retry/backoff, discarding the result like the legacy code did.
export function* refreshChecklists(checklistsUrl) {
  if (checklistsUrl) {
    try {
      yield call(requestLoopExp, { url: checklistsUrl, method: 'GET' });
    } catch {
      // discarded, same as the legacy behavior
    }
  }
}

export function* acceptRepairOrder({ payload }) {
  const { request } = payload;

  try {
    const { data: appointment } = yield call(restClient.request, request);
    yield put(actions.acceptRepairOrderSuccess(appointment));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'web.keydeposit.notification.confirm.success',
          defaultMessage: 'Works sent successfully.',
        },
      })
    );
  } catch (error) {
    yield put(actions.acceptRepairOrderFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'web.keydeposit.notification.confirm.error',
          defaultMessage: 'Cannot confirm works to accept.',
        },
      })
    );
  }
}

export function* confirmClientDocuments({ payload }) {
  const { request } = payload;

  try {
    const { data: appointment } = yield call(restClient.request, request);
    yield put(actions.confirmClientDocumentsSuccess(appointment));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'web.appointments.client-documents.upload.success',
          defaultMessage: 'Document(s) added',
        },
      })
    );
    const { checklistsUrl } = yield select(getClientDocumentsTask);
    yield call(refreshChecklists, checklistsUrl);
  } catch (error) {
    yield put(actions.confirmClientDocumentsFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'web.appointments.client-documents.upload.error',
          defaultMessage: 'Document(s) were not added',
        },
      })
    );
  }
}

export function* acceptRentalDocuments({ payload }) {
  const { request } = payload;

  try {
    const { data: appointment } = yield call(restClient.request, request);
    yield put(actions.acceptRentalDocumentsSuccess(appointment));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'web.appointments.rental-vehicle.documents.confirm.success',
          defaultMessage: 'Document(s) accepted',
        },
      })
    );
  } catch (error) {
    yield put(actions.acceptRentalDocumentsFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'web.appointments.rental-vehicle.documents.confirm.failure',
          defaultMessage: 'Cannot accept document(s)',
        },
      })
    );
  }
}

export function* reportIssues({ payload }) {
  const { request } = payload;

  try {
    const { data: appointment } = yield call(restClient.request, request);
    yield put(actions.reportIssuesSuccess(appointment));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'web.appointments.rental-vehicle.damages.confirm.success',
          defaultMessage: 'Issue(s) with rental vehicle reported',
        },
      })
    );
    const { checklistsUrl } = yield select(getRentalCarTask);
    yield call(refreshChecklists, checklistsUrl);
  } catch (error) {
    yield put(actions.reportIssuesFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'web.appointments.rental-vehicle.damages.confirm.failure',
          defaultMessage: 'Cannot report issue(s) with rental vehicle',
        },
      })
    );
  }
}

const CONFIRM_MESSAGES = {
  [actions.confirmDropOff.type]: {
    successAction: actions.confirmDropOffSuccess,
    failureAction: actions.confirmDropOffFailure,
    successMessage: { id: 'web.appointments.dropoff.confirm.success', defaultMessage: 'Key drop-off confirmed' },
    failureMessage: { id: 'web.appointments.dropoff.confirm.error', defaultMessage: 'Cannot confirm key drop-off' },
  },
  [actions.confirmPickUp.type]: {
    successAction: actions.confirmPickUpSuccess,
    failureAction: actions.confirmPickUpFailure,
    successMessage: { id: 'web.appointments.pickup.confirm.success', defaultMessage: 'Key pick up confirmed' },
    failureMessage: { id: 'web.appointments.pickup.confirm.error', defaultMessage: 'Cannot confirm key pick up' },
  },
};

export function* confirm({ type, payload }) {
  const { request } = payload;
  const { successAction, failureAction, successMessage, failureMessage } = CONFIRM_MESSAGES[type];

  try {
    const { data: appointment } = yield call(restClient.request, request);
    yield put(successAction(appointment));
    yield put(createNotification({ type: 'success', message: successMessage }));
  } catch (error) {
    yield put(failureAction(error));
    yield put(createNotification({ type: 'error', message: failureMessage }));
  }
}

const OPEN_LOCKER_ACTIONS = {
  [actions.openDropOffLocker.type]: {
    successAction: actions.openDropOffLockerSuccess,
    failureAction: actions.openDropOffLockerFailure,
  },
  [actions.openPickUpLocker.type]: {
    successAction: actions.openPickUpLockerSuccess,
    failureAction: actions.openPickUpLockerFailure,
  },
};

export function* openLocker({ type, payload }) {
  const { request } = payload;
  const { successAction, failureAction } = OPEN_LOCKER_ACTIONS[type];

  try {
    const { data: appointment } = yield call(restClient.request, request);
    yield put(successAction(appointment));
    yield put(
      createNotification({
        type: 'success',
        message: { id: 'web.appointments.dropoff.locker.open.success', defaultMessage: 'Locker opened' },
      })
    );
  } catch (error) {
    yield put(failureAction(error));
    const wrongLocation = error.response?.status === 400;
    yield put(
      createNotification({
        type: 'error',
        message: wrongLocation
          ? {
            id: 'web.appointments.dropoff.locker.open.locationId_mismatch',
            defaultMessage: 'The locationId does not match your locker',
          }
          : {
            id: 'web.appointments.dropoff.locker.open.error',
            defaultMessage: 'Error when opening the locker',
          },
      })
    );
  }
}

export function* payBilling({ payload }) {
  const { request } = payload;

  try {
    const { data: payment } = yield call(restClient.request, request);
    yield put(actions.payBillingSuccess());
    try {
      yield call(redirect, payment.redirectUrl);
    } catch {
      yield put(
        createNotification({
          type: 'error',
          message: { id: 'web.error.file.common', defaultMessage: 'An error has occurred.' },
        })
      );
    }
  } catch (error) {
    yield put(actions.payBillingFailure(error));
  }
}

export default function* appointmentSagas() {
  yield takeLatest(actions.acceptRepairOrder, acceptRepairOrder);
  yield takeLatest(actions.confirmClientDocuments, confirmClientDocuments);
  yield takeLatest(actions.acceptRentalDocuments, acceptRentalDocuments);
  yield takeLatest(actions.reportIssues, reportIssues);
  yield takeLatest([actions.confirmDropOff, actions.confirmPickUp], confirm);
  yield takeLatest([actions.openDropOffLocker, actions.openPickUpLocker], openLocker);
  yield takeLatest(actions.payBilling, payBilling);
}
